import { useState, useEffect, ChangeEvent, FC } from "react";
import Button from "../../Button/Button";
import InputBox from "../../Input/Input";
import Label from "../../Label/Label";
import TextArea from "../../TextArea/TextArea";
import Modal from "../Modal";
import { VerifyUserName } from "../../../services/username.service";
import { username } from "../../../types/types";
import useAuthService from "../../../auth/AuthService";

interface PublicInfoProps {
  isVisible?: boolean;
  onClose?: () => void;
  state?: any;
  handleUpdateInfo?: (userData: any) => void;
  handleCheckboxChange?: (event: any) => void;
}

const EditPublicInfo: FC<PublicInfoProps> = ({
  isVisible,
  onClose,
  state,
  handleUpdateInfo,
  handleCheckboxChange,
}) => {
  const { getToken } = useAuthService();
  const [formData, setFormData] = useState({
    username: state?.username || "",
    first_name: state?.first_name || "",
    last_name: state?.last_name || "",
    public_bio: state?.public_bio || "",
  });

  const [errors, setErrors] = useState({
    username: "",
    first_name: "",
    last_name: "",
    public_bio: "",
  });

  const [isUsernameSaved, setIsUsernameSaved] = useState(false);

  useEffect(() => {
    if (state) {
      setFormData({
        username: state.username || "",
        first_name: state.first_name || "",
        last_name: state.last_name || "",
        public_bio: state.public_bio || "",
      });
      setIsUsernameSaved(!!state.username);
    }
  }, [state]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let errorMsg = "";

    if (name === "username" && !isUsernameSaved) {
      // Username validation logic
      if (!value.trim()) {
        errorMsg = "Username is required.";
      } else if (value.length < 3 || value.length > 20) {
        errorMsg = "Username must be 3-20 characters.";
      } else if (/\s/.test(value)) {
        errorMsg = "Spaces are not allowed.";
      } else if (/[^a-zA-Z0-9_-]/.test(value)) {
        errorMsg =
          "Emojis and special characters (except _ and -) are not allowed.";
      } else if (/^\d+$/.test(value)) {
        errorMsg = "Username cannot be numbers only.";
      } else if (/^_|_$/.test(value)) {
        errorMsg = "Underscores (_) cannot be at the start or end.";
      } else {
        handleUserName(value);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        username: errorMsg,
      }));
    } else if (name !== "username") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: "", // Reset any error for other fields
      }));
    }
  };

  const handleUserName = async (value: string) => {
    const username: username = { username: value };
    const response = await VerifyUserName(getToken, username);
    if (response.error != null) {
      setErrors((prev) => ({
        ...prev,
        username:
          response.error || "There was an error with the username validation.", // Display backend error
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      isAvailable: true,
      errors: "",
      msg: response.data.message,
    }));
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (state?.username === "") {
      handleUserName(formData.username);
    }
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required.";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required.";
    }
    if (!formData.public_bio.trim()) {
      newErrors.public_bio = "Public bio is required.";
    } else if (
      formData.public_bio.length < 10 ||
      formData.public_bio.length > 300
    ) {
      newErrors.public_bio =
        "Public bio must be more than 10 and less than 300 characters.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = () => {
    if (validateForm() && !errors.username && handleUpdateInfo) {
      const userData: any = {
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        public_bio: formData.public_bio,
      };
      handleUpdateInfo(userData);
      setIsUsernameSaved(true);
    }
  };

  const handleClose = () => {
    if (onClose) {
      setErrors({
        username: "",
        first_name: "",
        last_name: "",
        public_bio: "",
      });
      onClose();
    }
  };

  const modalBody = (
    <>
      <div className="modal-body bg-modalbody m-3 pt-0 mt-0">
        <form className="account-form text-start">
          {!isUsernameSaved && (
            <div className="row mb-3">
              <Label
                className="col-12 col-form-label font-inter-400-15"
                text="Username"
                required={true}
              />
              <div className="col">
                <InputBox
                  onChange={handleInputChange}
                  className="form-control application-form-control"
                  placeholder="Username"
                  name="username"
                  value={formData.username}
                  disabled={isUsernameSaved} // Disable input if username is saved
                />
                {errors.username && (
                  <div className="err-msg">{errors.username}</div>
                )}
              </div>
            </div>
          )}
          <div className="row mb-3">
            <Label
              className="col-12 col-form-label font-inter-400-15"
              text="First Name"
              required={true}
            />
            <div className="col">
              <InputBox
                onChange={handleInputChange}
                className="form-control application-form-control"
                placeholder="First Name"
                name="first_name"
                value={formData.first_name}
              />
              {errors.first_name && (
                <div className="err-msg">{errors.first_name}</div>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <Label
              className="col-12 col-form-label font-inter-400-15"
              text="Last Name"
              required={true}
            />
            <div className="col">
              <InputBox
                onChange={handleInputChange}
                className="form-control application-form-control"
                placeholder="Last Name"
                name="last_name"
                value={formData.last_name}
              />
              {errors.last_name && (
                <div className="err-msg">{errors.last_name}</div>
              )}
            </div>
          </div>

          {/* <div className="mb-2 mw-50 mx-auto justify-content-center d-flex">
            <div className="text-end mt-1 pe-1">Make Full Name Public</div>
            <div className="form-check position-relative my-0 py-0">
              <InputBox
                className="form-check-input check-input"
                type="checkbox"
                name="isFullName"
                checked={state.isFullName}
                onChange={handleCheckboxChange}
              />
              <div
                className={`${!state.isFullName ? "circle" : "circle-checked"
                  }`}
              ></div>
            </div>
          </div> */}

          <div className="row mb-3">
            <div className="d-flex align-items-baseline justify-content-between">
              <Label
                className="col-form-label"
                text="Public Bio (max 300 characters)"
                required={true}
              />
              <div className="text-end small">
                {formData.public_bio.length} of 300
                {formData.public_bio.length >= 300 && (
                  <span className="text-danger"> (Limit exceeded)</span>
                )}
              </div>
            </div>
            <div className="col">
              <TextArea
                className="form-control application-form-control public-bio"
                placeholder="Public Bio"
                name="public_bio"
                onChange={handleInputChange}
                value={formData.public_bio.substring(0, 299)} // Ensure max length of 300
              />
              {errors.public_bio && (
                <div className="err-msg">{errors.public_bio}</div>
              )}
            </div>
          </div>

          <div className="mb-1 d-flex justify-content-center">
            <Button
              label={"Save Changes"}
              variant="primary"
              className="w-100 font-18 font-500"
              onClick={handleSaveChanges}
            />
          </div>
        </form>
      </div>
    </>
  );

  return (
    <Modal
      id="EditPublicInfo"
      title="Public Info"
      body={modalBody}
      modalBackground="modal-bg"
      onClose={handleClose}
      isVisible={isVisible}
    />
  );
};

export default EditPublicInfo;
